import { useCallback, useEffect, useState } from "react";
import { Building2, Check, Globe, X } from "lucide-react";
import { toast } from "sonner";
import { PageHeader } from "@/components/AppShell";
import { Spinner } from "@/components/Gate";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { errMsg, fileUrl, useApi } from "@/lib/api";

export default function AdminEmployers() {
  const api = useApi();
  const [status, setStatus] = useState("pending");
  const [list, setList] = useState(null);
  const [rejecting, setRejecting] = useState(null);
  const [reason, setReason] = useState("");

  const load = useCallback(() => {
    setList(null);
    api.get("/admin/employers", { params: { status } }).then((r) => setList(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [api, status]);
  useEffect(() => { load(); }, [load]);

  const decide = async (userId, decision, why = "") => {
    try {
      await api.post(`/admin/employers/${userId}/decision`, { decision, reason: why });
      toast.success(`Employer ${decision}`);
      setRejecting(null);
      setReason("");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div>
      <PageHeader eyebrow="Admin" title="Employer approvals" />
      <Tabs value={status} onValueChange={setStatus} className="mb-6">
        <TabsList>
          {["pending", "approved", "rejected"].map((s) => <TabsTrigger key={s} value={s} className="capitalize" data-testid={`employers-tab-${s}`}>{s}</TabsTrigger>)}
        </TabsList>
      </Tabs>
      {!list ? <Spinner /> : (
        <div className="grid gap-4 md:grid-cols-2" data-testid="employers-list">
          {list.map((e) => (
            <div key={e.user_id} className="rounded-2xl border border-slate-200 bg-white p-5" data-testid={`employer-card-${e.user_id}`}>
              <div className="flex gap-4">
                <div className="grid h-14 w-14 shrink-0 place-items-center overflow-hidden rounded-lg border border-slate-200 bg-slate-50">
                  {e.logo_file_id ? <img src={fileUrl(`/api/files/${e.logo_file_id}`)} alt="" className="h-full w-full object-contain" /> : <Building2 className="h-6 w-6 text-slate-400" />}
                </div>
                <div className="min-w-0">
                  <p className="font-display text-lg font-bold text-slate-900">{e.company_name}</p>
                  <p className="text-sm text-slate-600">{e.industry} · {e.size} · {e.location}</p>
                  <p className="text-xs text-slate-500">{e.contact_person}{e.contact_title && `, ${e.contact_title}`} · {e.contact_email}</p>
                  {e.website && <a href={e.website} target="_blank" rel="noreferrer" className="mt-1 inline-flex items-center gap-1 text-xs text-[#222d99] underline"><Globe className="h-3 w-3" /> {e.website}</a>}
                </div>
              </div>
              {e.about && <p className="mt-3 line-clamp-3 text-sm text-slate-600">{e.about}</p>}
              {e.decision_reason && <p className="mt-2 text-xs text-slate-500">Note: {e.decision_reason}</p>}
              <div className="mt-4 flex gap-2">
                {e.approval_status !== "approved" && <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => decide(e.user_id, "approved")} data-testid={`employer-approve-${e.user_id}`}><Check className="mr-1 h-4 w-4" /> Approve</Button>}
                {e.approval_status !== "rejected" && <Button size="sm" variant="outline" className="border-rose-300 text-rose-700" onClick={() => setRejecting(e)} data-testid={`employer-reject-${e.user_id}`}><X className="mr-1 h-4 w-4" /> Reject</Button>}
              </div>
            </div>
          ))}
          {!list.length && <p className="col-span-full rounded-2xl border border-dashed border-slate-300 p-10 text-center text-sm text-slate-500">No {status} employers.</p>}
        </div>
      )}
      <Dialog open={!!rejecting} onOpenChange={(o) => !o && setRejecting(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Reject {rejecting?.company_name}?</DialogTitle></DialogHeader>
          <Textarea value={reason} onChange={(e) => setReason(e.target.value)} maxLength={500} placeholder="Reason shared with the employer" data-testid="employer-reject-reason" />
          <DialogFooter><Button variant="destructive" onClick={() => decide(rejecting.user_id, "rejected", reason)} data-testid="employer-reject-confirm">Reject</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
